<script setup lang="ts">
import { ref, onMounted, onUnmounted, type ComponentInstance } from 'vue'

const APP_VERSION = __APP_VERSION__
import {
  SiddChatBot,
  ApiError,
  Position,
  logger,
  WebDirectSidebar,
  WebDirectSidebarDrawer,
} from '@sidd/chatbot-core'
import type { IAnalyticsCallback } from '@sidd/chatbot-core'

const chatbotRef = ref<ComponentInstance<typeof SiddChatBot> | null>(null)
import WebDirectError from '../WebDirectError/WebDirectError.vue'
import siddFaceSvg from '../../assets/sidd-face.svg'

const MOBILE_BREAKPOINT = 768

interface IWebDirectLayoutProps {
  readonly logoUrl?: string
  readonly name?: string
  readonly isAuthenticated?: boolean
  readonly userName?: string | null
  readonly userDisplayName?: string | null
  readonly userId?: string | null
  readonly chatbotId: string
  readonly tenantId: string
  readonly getAuthInfo?: () => Promise<{
    accessToken: string
    userTenant: string
    userId?: string
  }>
  readonly analyticsCallback?: IAnalyticsCallback
}

interface IWebDirectLayoutEmits {
  logout: []
}

const props = defineProps<IWebDirectLayoutProps>()
const emit = defineEmits<IWebDirectLayoutEmits>()

const isMobile = ref(window.innerWidth < MOBILE_BREAKPOINT)
const isSidebarOpen = ref(false)

function handleResize() {
  isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
  if (!isMobile.value) isSidebarOpen.value = false
}

onMounted(() => window.addEventListener('resize', handleResize))
onUnmounted(() => window.removeEventListener('resize', handleResize))

interface IChatbotError {
  readonly type: 'forbidden' | 'not-found' | 'error'
}

const chatbotError = ref<IChatbotError | null>(null)

function handleChatbotError(err: Error): void {
  if (err instanceof ApiError) {
    if (err.isUnauthorized || err.isForbidden) {
      chatbotError.value = { type: 'forbidden' }
    } else if (err.isNotFound) {
      chatbotError.value = { type: 'not-found' }
    } else {
      chatbotError.value = { type: 'error' }
    }
  } else {
    chatbotError.value = { type: 'error' }
  }
  logger.error('Chatbot error', err, { component: 'WebDirectLayout' })
}

function handleRetry(): void {
  chatbotError.value = null
}

async function handleLogout(): Promise<void> {
  isSidebarOpen.value = false
  await chatbotRef.value?.endSession()
  emit('logout')
}
</script>

<template>
  <div class="flex w-full h-dvh overflow-hidden bg-background">
    <WebDirectSidebar
      v-if="!isMobile"
      :logo-url="logoUrl"
      :fallback-logo-url="siddFaceSvg"
      :name="name"
      :is-authenticated="isAuthenticated"
      :user-name="userName"
      :user-display-name="userDisplayName"
      :app-version="APP_VERSION"
      @logout="handleLogout"
    />

    <main
      class="wd-chat-panel flex-1 min-w-0 flex flex-col overflow-hidden bg-background"
      role="main"
      aria-label="Chat conversation"
    >
      <WebDirectError
        v-if="chatbotError"
        :type="chatbotError.type"
        :inline="true"
        @retry="handleRetry"
      />
      <SiddChatBot
        v-else
        ref="chatbotRef"
        :chatbot-id="chatbotId"
        :tenant-id="tenantId"
        :user-id="userId || undefined"
        :get-auth-info="getAuthInfo"
        :analytics-callback="analyticsCallback"
        :override-configuration="{
          position: Position._FillParent,
          autoOpen: true,
        }"
        @menu="isSidebarOpen = true"
        @error="handleChatbotError"
      />
    </main>

    <WebDirectSidebarDrawer
      v-model:open="isSidebarOpen"
      :logo-url="logoUrl"
      :fallback-logo-url="siddFaceSvg"
      :name="name"
      :is-authenticated="isAuthenticated"
      :user-name="userName"
      :user-display-name="userDisplayName"
      :app-version="APP_VERSION"
      @logout="handleLogout"
    />
  </div>
</template>

<style scoped>
/* !important required to neutralise the widget's own fixed/absolute positioning */
.wd-chat-panel :deep(.siddchatbot-widget) {
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
  inset: auto !important;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
}

.wd-chat-panel :deep(.sidd-chat-window) {
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
}

.wd-chat-panel :deep(.sidd-chat-container) {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

/* Hide sidebar trigger when the persistent sidebar is already visible */
@media (min-width: 768px) {
  .wd-chat-panel :deep(.sidd-chat-header__menu) {
    display: none;
  }
}
</style>
